import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { SEARCE_COLORS } from '../../theme';
// Using public folder for static assets
const logo = '/Logo.jpg';

const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state or default to dashboard
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setIsAuthenticating(true);
      setError(null);

      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }

      await login(credentialResponse.credential);

      // Redirect to the page they were trying to visit or dashboard
      navigate(from, { replace: true });
    } catch (err) {

      // Enhanced error handling
      let errorMessage = 'Login failed. Please try again.';

      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          errorMessage = 'Access denied. Please contact your administrator to request access.';
        } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'Your account is not authorized to access this dashboard.';
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('@searce.com')) {
          errorMessage = 'Only @searce.com email addresses are allowed.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again or contact support if the issue persists.');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${SEARCE_COLORS.primary}15 0%, ${SEARCE_COLORS.secondary}15 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            background: SEARCE_COLORS.white,
            boxShadow: '0 20px 40px rgba(0, 100, 255, 0.1)',
          }}
        >
          {/* Logo/Brand Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="Searce Logo"
              sx={{
                height: { xs: 60, sm: 80, md: 100 },
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                mb: 2,
                display: 'block',
                mx: 'auto',
              }}
            />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                fontFamily: ['Inter', 'SF Pro Display', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
                color: SEARCE_COLORS.textPrimary,
                mb: 2,
                letterSpacing: '0.01em',
                lineHeight: 1.3,
                textAlign: 'center',
              }}
            >
              Delivery Pulse
            </Typography>
          </Box>

          {/* Login Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: SEARCE_COLORS.textPrimary,
                fontWeight: 600,
              }}
            >
              Sign in to your account
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                mb: 3,
                color: SEARCE_COLORS.textSecondary,
              }}
            >
              Use your Searce email to access the dashboard
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Google Login Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              '& > div': {
                boxShadow: `0 4px 12px ${SEARCE_COLORS.primary}20 !important`,
              }
            }}
          >
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              size="large"
              text="signin_with"
              theme="outline"
              shape="rectangular"
            />
          </Box>

          {/* Footer Info */}
          <Box sx={{ pt: 3, borderTop: `1px solid ${SEARCE_COLORS.neutral_200}` }}>
            {/* <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                fontSize: '0.75rem',
                display: 'block',
                mb: 1,
              }}
            >
              Only users with @searce.com email addresses can access this dashboard.
            </Typography> */}
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                fontSize: '0.70rem',
              }}
            >
              {/* COMMENTED OUT: Whitelist restriction message disabled */}
              {/* Access is restricted to whitelisted users only. Contact your administrator if you need access. */}
              Access is available to all Searce employees. Please use your @searce.com email to sign in.
            </Typography>
          </Box>

          {/* Help Text */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                fontSize: '0.70rem',
                display: 'block',
              }}
            >
              Having trouble signing in? Contact your administrator.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
